import { InfoCircleOutlined, KeyOutlined, LockOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Checkbox, Form, Input, message, Modal, Tag } from 'antd';
import React from 'react';
import { OrganizationService } from '../services/organization';

interface RegisterFormProps {
  onRegister: (username: string, password: string, invitationCode: string) => Promise<void>;
  onBackToLogin: () => void;
  showBackButton?: boolean;
}

/**
 * 用户注册表单组件
 * 
 * @param onRegister 注册回调函数
 * @param onBackToLogin 返回登录页面的回调函数
 */
const RegisterForm: React.FC<RegisterFormProps> = ({ onRegister, onBackToLogin, showBackButton = true }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [disclaimerVisible, setDisclaimerVisible] = React.useState(false);
  const [inviteCodeInfo, setInviteCodeInfo] = React.useState<{
    organization: {
      id: number;
      name: string;
      description?: string;
      status: string;
    };
    role: 'admin' | 'worker';
  } | null>(null);
  const [inviteCodeValidating, setInviteCodeValidating] = React.useState(false);

  const validateInviteCode = async (code: string) => {
    if (!code) {
      setInviteCodeInfo(null);
      return;
    }

    setInviteCodeValidating(true);
    try {
      const response = await OrganizationService.validateInviteCode(code);
      setInviteCodeInfo(response);
    } catch (error: any) {
      setInviteCodeInfo(null);
      if (error.response?.status !== 404) {
        message.error('验证邀请码失败');
      }
    } finally {
      setInviteCodeValidating(false);
    }
  };

  const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const code = e.target.value;
    if (code) {
      const debounceTimer = setTimeout(() => validateInviteCode(code), 500);
      return () => clearTimeout(debounceTimer);
    } else {
      setInviteCodeInfo(null);
    }
  };

  const handleSubmit = async (values: { username: string; password: string; confirmPassword: string; invitationCode: string; agreementAccepted: boolean }) => {
    if (values.password !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    if (!values.agreementAccepted) {
      message.error('请阅读并同意用户协议和免责声明');
      return;
    }

    setLoading(true);
    try {
      await onRegister(values.username, values.password, values.invitationCode);
      message.success('注册成功！请使用新账号登录');
      form.resetFields();
      onBackToLogin();
    } catch (error: any) {
      message.error(error.response?.data?.error || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  // 免责声明内容
  const disclaimerContent = (
    <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '0 8px' }}>
      <h3>用户服务协议与免责声明</h3>
      
      <h4>【服务性质说明】</h4>
      <ul>
        <li>本系统仅为技术工具，提供基于人工智能的文本生成服务</li>
        <li>所有解读内容均为算法计算结果，不涉及任何超自然现象</li>
        <li>本系统仅供娱乐参考使用，不构成专业建议或指导</li>
        <li>用户应理性看待系统输出内容，独立思考和判断</li>
      </ul>

      <h4>【免责条款】</h4>
      <ul>
        <li>本系统不保证解读结果的准确性、完整性或适用性</li>
        <li>用户基于系统输出做出的任何决策均与本系统无关</li>
        <li>本系统不承担因使用服务而产生的任何直接或间接损失</li>
        <li>本系统不对用户的商业行为承担任何责任</li>
      </ul>

      <h4>【使用限制】</h4>
      <ul>
        <li>用户不得将系统用于任何违法违规活动</li>
        <li>用户不得散布迷信思想或进行欺诈活动</li>
        <li>用户应遵守相关法律法规和社会公德</li>
        <li>系统保留随时终止服务的权利</li>
      </ul>

      <h4>【数据安全】</h4>
      <ul>
        <li>用户数据仅用于系统功能实现，不会泄露给第三方</li>
        <li>用户输入的信息将进行安全加密处理</li>
        <li>系统具备完善的内容安全审核机制</li>
      </ul>

      <h4>【其他条款】</h4>
      <ul>
        <li>本协议的解释权归系统开发方所有</li>
        <li>如有争议，以中华人民共和国法律为准</li>
        <li>本协议可能随时更新，用户应定期查阅</li>
      </ul>

      <p style={{ marginTop: '16px', fontWeight: 'bold', color: '#1890ff' }}>
        使用本系统即表示您已阅读、理解并同意遵守上述所有条款。
      </p>
    </div>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: showBackButton ? '60vh' : 'auto' }}>
      <Card title={showBackButton ? "用户注册" : undefined} style={{ width: 400, border: showBackButton ? undefined : 'none', boxShadow: showBackButton ? undefined : 'none' }}>
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="invitationCode"
            rules={[
              { required: true, message: '请输入邀请码' },
              { pattern: /^[A-Z0-9]+$/, message: '邀请码格式不正确，只能包含大写字母和数字' }
            ]}
          >
            <Input
              prefix={<KeyOutlined />}
              placeholder="邀请码（必填）"
              size="large"
              onChange={handleInviteCodeChange}
              suffix={inviteCodeValidating ? <InfoCircleOutlined spin /> : null}
            />
          </Form.Item>
          
          {inviteCodeInfo && (
            <div style={{ marginBottom: 16 }}>
              <Alert
                message="邀请码验证成功"
                description={
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <TeamOutlined style={{ marginRight: 8 }} />
                      <strong>组织：{inviteCodeInfo.organization.name}</strong>
                      <Tag 
                        color={inviteCodeInfo.organization.status === 'active' ? 'green' : 'orange'} 
                        style={{ marginLeft: 8 }}
                      >
                        {inviteCodeInfo.organization.status === 'active' ? '正常' : '暂停'}
                      </Tag>
                    </div>
                    <div>
                      <strong>角色：</strong>
                      <Tag color={inviteCodeInfo.role === 'admin' ? 'red' : 'blue'}>
                        {inviteCodeInfo.role === 'admin' ? '管理员' : '工作者'}
                      </Tag>
                    </div>
                    {inviteCodeInfo.organization.description && (
                      <div style={{ marginTop: 8, color: '#666' }}>
                        {inviteCodeInfo.organization.description}
                      </div>
                    )}
                  </div>
                }
                type="success"
                showIcon
              />
            </div>
          )}
          
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' },
              { max: 20, message: '用户名最多20个字符' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名（3-20个字符）"
              size="large"
            />
          </Form.Item>
          
          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码（至少6个字符）"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="确认密码"
              size="large"
            />
          </Form.Item>

          {/* 免责声明复选框 */}
          <Form.Item
            name="agreementAccepted"
            valuePropName="checked"
            rules={[
              { 
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('请阅读并同意用户协议和免责声明'))
              }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Checkbox>
              我已阅读并同意
              <Button
                type="link"
                style={{ padding: '0 4px', fontSize: '14px' }}
                onClick={() => setDisclaimerVisible(true)}
              >
                《用户服务协议与免责声明》
              </Button>
            </Checkbox>
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large" 
              block 
              loading={loading}
            >
              注册
            </Button>
          </Form.Item>

          {showBackButton && (
            <Form.Item>
              <Button 
                type="link" 
                onClick={onBackToLogin}
                block
              >
                已有账号？返回登录
              </Button>
            </Form.Item>
          )}
        </Form>
        
        <div style={{ marginTop: 16, fontSize: '12px', color: '#666', textAlign: 'center' }}>
          <p>使用邀请码注册将加入对应组织</p>
          <p>角色权限由邀请码类型决定</p>
          <p style={{ color: '#ff4d4f' }}>
            <InfoCircleOutlined /> 本系统仅供娱乐参考，不构成专业建议
          </p>
        </div>
      </Card>

      {/* 免责声明弹窗 */}
      <Modal
        title="用户服务协议与免责声明"
        open={disclaimerVisible}
        onCancel={() => setDisclaimerVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setDisclaimerVisible(false)}>
            我已了解
          </Button>
        ]}
        width={600}
      >
        {disclaimerContent}
      </Modal>
    </div>
  );
};

export default RegisterForm;
